/**
 * preferences-rpc-client — the caller's own preferences over auth.v1, plus the
 * identity summary the profile page shows. Both endpoints act only on the
 * authenticated caller (godev auth-service/preferences.go), so there is no user
 * id to pass. FEATURE-20260915-user-profile-page-and-preferences.
 */
import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import type { GetUserInfoForSelfResponse } from "../hermesclient/proto/auth";
import { asLogicalMailbox } from "../hermesclient/types";
import { nameMappingService } from "./NameMappingService";
import { Duration } from "../duration";
import { setPreferences, type UserPreferences } from "./user-preferences";

async function authRpc<TResp>(endPoint: string, request: object): Promise<TResp> {
  const authMailbox = await nameMappingService.resolve(asLogicalMailbox("auth"));
  const resp = await GlobalClient.get().rawRpcCall({
    to: authMailbox,
    endPoint,
    body: new TextEncoder().encode(JSON.stringify(request)),
    contentType: ContentType.Json,
  });
  if (resp.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = resp.header?.rpcHeader?.errorInfo;
    throw new Error(
      errorInfo?.message || errorInfo?.errorNode?.frame?.message || `${endPoint} failed`,
    );
  }
  if (!resp.data || resp.data.length === 0) {
    return {} as TResp;
  }
  return JSON.parse(new TextDecoder().decode(resp.data)) as TResp;
}

interface PreferencesResponse {
  preferences?: UserPreferences;
}

// How long a login waits for preferences before landing on the default page.
// Preferences only choose WHERE to land; they must never be why a login hangs.
const LOAD_TIMEOUT = Duration.fromSeconds(5);

let inflight: Promise<void> | null = null;

/**
 * Fetches the caller's preferences into the store. Never throws: on an error or
 * a timeout (an older mesh without the endpoint, a slow socket) the store keeps
 * what it had and the app uses its defaults.
 */
export function loadPreferences(): Promise<void> {
  if (!inflight) {
    const fetched = authRpc<PreferencesResponse>("auth.GetPreferencesForSelf", {})
      .then((resp) => setPreferences(resp.preferences ?? {}))
      .catch((err) => console.warn("[preferences] load failed; using defaults", err));
    const timeout = new Promise<void>((resolve) =>
      setTimeout(resolve, LOAD_TIMEOUT.toMilliseconds()),
    );
    inflight = Promise.race([fetched, timeout]).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

/** Replaces the caller's preferences; the store takes what the server stored. */
export async function savePreferences(prefs: UserPreferences): Promise<UserPreferences> {
  const resp = await authRpc<PreferencesResponse>("auth.UpdatePreferencesForSelf", {
    preferences: prefs,
  });
  const stored = resp.preferences ?? {};
  setPreferences(stored);
  return stored;
}

export function fetchUserInfoForSelf(): Promise<GetUserInfoForSelfResponse> {
  return authRpc<GetUserInfoForSelfResponse>("auth.GetUserInfoForSelf", {});
}
